import React, { FC, useCallback, useMemo, useReducer } from 'react';
import classnames from 'classnames';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';

import { Button } from '@clearcover/atlas';
import { Column, FormInput, Modal, Row } from 'Shared/components';

import { useAppContext } from 'QuoteApp/services/providers';
import {
  DriverFieldsType,
  GetDetailsResponseType,
  VehicleFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  formatDriverDisplay,
  getDriverDataByFieldValue,
  getFieldErrors,
  getVehicleDataByFieldValue,
  yupVinValidator,
} from 'QuoteApp/utils';
import { transformEntitiesRequest } from 'QuoteApp/services/endpoints/PostSaveSummary';
import { VinNotFoundModal } from 'QuoteApp/components/VinNotFoundModal';

import { VinInput } from './VinInput';
import { reducer, initialState } from './vinReducer';
import styles from './styles.module.css';

type Props = {
  show: boolean;
  onSuccess(newDetails: GetDetailsResponseType): void;
  onCancel(): void;
};

/**
 * Prompt for missing data if applicable after CLUE & MVR are approved
 */
export const MissingDataModal: FC<Props> = ({ show, onSuccess, onCancel }) => {
  const { config, details, setDetails, api } = useAppContext();
  const [vinState, vinDispatch] = useReducer(reducer, initialState);

  const serverErrors = useMemo(
    () => ({
      drivers: getDriverDataByFieldValue(details.drivers, 'errors'),
      vehicles: getVehicleDataByFieldValue(details.vehicles, 'errors'),
    }),
    [details.drivers, details.vehicles]
  );

  const editableFieldData = useMemo(
    () => ({
      drivers: getDriverDataByFieldValue(details.drivers, 'editable'),
      vehicles: getVehicleDataByFieldValue(details.vehicles, 'editable'),
    }),
    [details.drivers, details.vehicles]
  );

  const driverFormInitialValues = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'value'),
    [details.drivers]
  );

  const driverDataFieldsMasked = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'masked'),
    [details.drivers]
  );

  const isSubmittedDataInvalid = useCallback(
    (newDetails: GetDetailsResponseType) => {
      return (
        Object.keys(newDetails.drivers).some(driverId => {
          return (
            newDetails.drivers[driverId].licenseState.errors?.length ||
            newDetails.drivers[driverId].licenseNumber.errors?.length
          );
        }) ||
        Object.keys(newDetails.vehicles).some(vehicleId => {
          return newDetails.vehicles[vehicleId].vin.errors?.length;
        })
      );
    },
    []
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        drivers: Yup.object(
          Object.keys(details.drivers).reduce(
            (
              acc: Record<
                string,
                Yup.ObjectSchema<
                  Yup.Shape<
                    object,
                    { licenseState: string; licenseNumber: string }
                  >
                >
              >,
              driverId
            ) => {
              if (details.drivers[driverId].driverType !== 'excludedPerson') {
                acc[driverId] = Yup.object().shape({
                  licenseState: Yup.string().required('Required'),
                  licenseNumber: Yup.string().required('Required'),
                });
              }
              return acc;
            },
            {}
          )
        ),
        vehicles: Yup.object(
          Object.keys(details.vehicles).reduce(
            (
              acc: Record<
                string,
                Yup.ObjectSchema<Yup.Shape<object, { vin: string }>>
              >,
              vehicleId
            ) => {
              acc[vehicleId] = Yup.object().shape({
                vin: yupVinValidator().required('Required'),
              });
              return acc;
            },
            {}
          )
        ),
      }),
    [details.drivers, details.vehicles]
  );

  const getEditedVehicles = useCallback(
    (vehicles: Record<string, VehicleFieldsType>) => {
      return Object.keys(details.vehicles).reduce(
        (acc: Record<string, VehicleFieldsType>, vehicleId) => {
          if (
            details.vehicles[vehicleId].vin.value !== vehicles[vehicleId].vin
          ) {
            return {
              ...acc,
              [vehicleId]: vehicles[vehicleId],
            };
          }
          return acc;
        },
        {}
      );
    },
    [details.vehicles]
  );

  const getEditedDrivers = useCallback(
    (drivers: Record<string, DriverFieldsType>) => {
      return Object.values(drivers).reduce((acc, driver) => {
        if (
          driver.licenseNumber ===
            details.drivers[driver.identifier].licenseNumber.value &&
          driver.licenseState ===
            details.drivers[driver.identifier].licenseState.value
        ) {
          return acc;
        }
        return {
          ...acc,
          [driver.identifier]: { ...driver },
        };
      }, {});
    },
    [details.drivers]
  );

  if (!show || !config) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        drivers: getDriverDataByFieldValue(details.drivers, 'value'),
        vehicles: getVehicleDataByFieldValue(details.vehicles, 'value'),
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        return api
          .saveAppData({
            data: {
              values: {
                drivers: getEditedDrivers(values.drivers),
                vehicles: getEditedVehicles(values.vehicles),
              },
              initialValues: {
                drivers: driverFormInitialValues,
              },
              editableFields: editableFieldData,
              maskedFields: {
                drivers: driverDataFieldsMasked,
              },
            },
            transformRequest: transformEntitiesRequest,
          })
          .then(response => {
            setDetails(response.data);

            if (!isSubmittedDataInvalid(response.data)) {
              onSuccess(response.data);
            }
          })
          .catch(() => setSubmitting(false));
      }}
    >
      {({
        dirty,
        errors,
        getFieldProps,
        handleSubmit,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldError,
        touched,
        values,
        initialValues,
      }) => (
        <>
          <VinNotFoundModal
            open={vinState.showModal}
            vin={Object.values(vinState.vins).join(', ')}
            onClose={() => {
              vinDispatch({
                type: 'closeModal',
              });
            }}
          />

          <Modal
            showModal={true}
            data-testid="missing-data-modal"
            className={styles.modalContainer}
          >
            <div className={styles.header}>
              <h4 className={styles.title}>Missing or Invalid Information</h4>
              <div>
                We are unable to run these reports due to missing or invalid
                information. Please provide the information below.
              </div>
            </div>
            <div className={styles.formWrapper}>
              <div className={styles.driverWrapper}>
                {Object.entries(values.drivers).map(([driverId, driver], i) =>
                  driver.driverType !== 'excludedPerson' ? (
                    <div className={styles.driver} key={`${driverId}`}>
                      <h3 className={classnames('fs-mask', styles.title)}>
                        {formatDriverDisplay(driver, config)}
                      </h3>
                      <Row>
                        <Column variant="half">
                          <FormInput
                            name={`drivers.${driverId}.licenseState`}
                            value={values.drivers[driverId].licenseState || ''}
                            variant="selectAutocomplete"
                            data-testid={`license-state-${i + 1}`}
                            label="Drivers License State"
                            onChange={value =>
                              setFieldValue(
                                `drivers.${driverId}.licenseState`,
                                value
                              )
                            }
                            options={config?.drivers.driverLicense.state ?? {}}
                            errorMessage={getFieldErrors(
                              `drivers.${driverId}.licenseState`,
                              true,
                              true,
                              errors,
                              serverErrors,
                              values,
                              touched,
                              initialValues
                            )}
                            disabled={
                              !getIn(
                                editableFieldData,
                                `drivers.${driverId}.licenseState`
                              )
                            }
                          />
                        </Column>
                        <Column variant="half">
                          <FormInput
                            {...getFieldProps(
                              `drivers.${driverId}.licenseNumber`
                            )}
                            name={`drivers.${driverId}.licenseNumber`}
                            value={values.drivers[driverId].licenseNumber || ''}
                            variant="lockedText"
                            data-testid={`license-number-${i + 1}`}
                            label="Drivers License Number"
                            errorMessage={getFieldErrors(
                              `drivers.${driverId}.licenseNumber`,
                              true,
                              true,
                              errors,
                              serverErrors,
                              values,
                              touched,
                              initialValues
                            )}
                            disabled={
                              !getIn(
                                editableFieldData,
                                `drivers.${driverId}.licenseNumber`
                              )
                            }
                          />
                        </Column>
                      </Row>
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.vehicleWrapper}>
                {Object.entries(values.vehicles).map(
                  ([vehicleId, vehicle], i) => (
                    <div
                      className={styles.vehicle}
                      key={`${vehicle.identifier}`}
                    >
                      <h3 className={styles.title}>
                        {`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      </h3>
                      <VinInput
                        {...getFieldProps(`vehicles.${vehicle.identifier}.vin`)}
                        vehicleId={vehicleId}
                        onChange={vehicleData => {
                          setFieldValue(`vehicles.${vehicleId}`, vehicleData);
                        }}
                        onFieldError={(errorMessage: string) => {
                          setFieldError(
                            `vehicles.${vehicleId}.vin`,
                            errorMessage
                          );
                        }}
                        name={`vehicles.${vehicleId}.vin`}
                        vehicleFields={values.vehicles[vehicleId]}
                        value={values.vehicles[vehicleId].vin || ''}
                        data-testid={`vin-${i + 1}`}
                        label="VIN"
                        onVinError={vin => {
                          vinDispatch({
                            type: 'setVinError',
                            vehicleId,
                            vin,
                          });
                        }}
                        onVinSuccess={() => {
                          vinDispatch({
                            type: 'setVinSuccess',
                            vehicleId,
                          });
                        }}
                        errorMessage={getFieldErrors(
                          `vehicles.${vehicleId}.vin`,
                          true,
                          true,
                          errors,
                          serverErrors,
                          values,
                          touched,
                          initialValues
                        )}
                        disabled={
                          !getIn(editableFieldData, `vehicles.${vehicleId}.vin`)
                        }
                      />
                    </div>
                  )
                )}
              </div>
            </div>
            <div className={styles.footer}>
              <Button
                onClick={() => {
                  vinDispatch({
                    type: 'reset',
                  });
                  onCancel();
                }}
                data-testid="cancel-missing-data-modal"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-missing-data-modal"
                type="submit"
                onClick={() => handleSubmit()}
                variant="primary"
                isSubmitting={isSubmitting}
                disabled={!dirty || !isValid}
              >
                Submit &amp; Run Reports
              </Button>
            </div>
          </Modal>
        </>
      )}
    </Formik>
  );
};
